import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowRight, CheckCheck, DollarSign, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../utils/api';
import { storedPermissionGranted } from '../../context/RBACContext';

const K = 'provider.messaging.readiness';

// Who the provider must talk to. `clinic_admin` is the only role that can
// change banking or PSP configuration; `treatx` means nobody at the clinic can
// fix it, so we must not send them to a settings page that cannot help.
//
// PAYMENT-SETUP-LINK-01 — the destination is `/clinic/profile`, which is where
// bank accounts and payout settings actually live, and it is admitted ONLY for
// `clinic.manage` holders. Two defects are closed here: it was rendered as a
// plain <a href>, which under this app's HashRouter left the SPA and landed on
// the public landing page (the hash route is navigated instead); and it was shown to a Sales Rep, who cannot open
// that page at all. Non-admins see the actor instruction instead.
const ACTOR_LINK = { clinic_admin: '/clinic/profile' };
// The route itself is guarded by `clinic.manage`; the same canonical permission
// (hydrated from the server at login) decides whether the link is offered.
const SETUP_PERMISSION = 'clinic.manage';

export const PaymentReadinessBar = ({ quote, onRequested }) => {
  const { t } = useTranslation();
  const canOpenSetup = storedPermissionGranted(SETUP_PERMISSION);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!quote?.id) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/clinic/quotes/${quote.id}/payment-readiness`);
      setReadiness(r.data?.readiness || null);
    } catch (e) {
      setError(e?.response?.data?.detail || null);
      setReadiness(null);
    } finally {
      setLoading(false);
    }
  }, [quote?.id]);

  useEffect(() => { load(); }, [load]);

  const requestPayment = useCallback(async () => {
    setRequesting(true);
    try {
      const r = await api.post(`/clinic/quotes/${quote.id}/request-payment`, {});
      if (r.data?.success) { onRequested?.(); return; }
      setReadiness(r.data?.readiness || readiness);
    } catch (e) {
      // The server re-evaluates at the mutation boundary, so a 409 here is the
      // authoritative answer — show it rather than the stale optimistic view.
      const body = e?.response?.data;
      if (body?.readiness) setReadiness(body.readiness);
      setError(body?.detail || null);
    } finally {
      setRequesting(false);
    }
  }, [quote?.id, onRequested, readiness]);

  if (!quote?.id) return null;

  if (loading) {
    return (
      <div className="bg-white border-t px-4 py-2.5 flex items-center gap-2"
           data-testid="payment-readiness-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
        <span className="text-xs text-slate-500">{t(`${K}.checking`)}</span>
      </div>
    );
  }

  // The server sends the canonical milestone amount as an exact decimal
  // STRING. `??` (not `||`) so a legitimate zero is never replaced.
  const amount = readiness?.amount ?? quote.financial_summary?.deposit ?? null;
  const currency = readiness?.currency || quote.currency || '';
  const line = t('provider.messaging.paymentQuoteLine', {
    treatment: quote.treatment_name,
    currency,
    amount: amount === null || amount === '' || Number.isNaN(Number(amount))
      ? '—' : Number(amount).toLocaleString(),
  });

  if (readiness?.ready) {
    return (
      <div className="bg-white border-t px-4 py-2.5 flex items-center gap-3"
           data-testid="payment-readiness-ready">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-500 truncate">{line}</p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700"
                  data-testid="readiness-confirmed">
              <CheckCheck className="h-3 w-3" />{t(`${K}.confirmed`)}
            </span>
            {readiness.settlement_currency && (
              <span className="text-[11px] text-slate-500"
                    data-testid="readiness-settlement-currency">
                {t(`${K}.settlesIn`, { currency: readiness.settlement_currency })}
              </span>
            )}
            {readiness.conversion_required && (
              <span className="text-[11px] text-amber-700" data-testid="readiness-fx-badge">
                {t(`${K}.conversionApplies`, {
                  from: currency, to: readiness.settlement_currency,
                })}
              </span>
            )}
          </div>
        </div>
        <Button onClick={requestPayment} disabled={requesting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-4 h-8 shrink-0"
                data-testid="request-payment-btn">
          {requesting ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      : <DollarSign className="h-3 w-3 mr-1" />}
          {t('provider.messaging.requestPayment')}
        </Button>
      </div>
    );
  }

  const blockers = readiness?.blockers || [];
  const primary = blockers[0];
  const link = (primary && canOpenSetup) ? ACTOR_LINK[primary.actor] : null;

  // MESSAGING-READINESS-TRUTH-01 — a milestone the PSP has already collected is
  // not a blocked action: it is done. It is reported as collected, with no
  // Request-payment control at all, so the same deposit can never be asked for
  // twice. A genuinely unpaid later instalment is unaffected — the canonical
  // schedule decides that, not this bar.
  if (readiness?.deposit_collected) {
    return (
      <div className="bg-emerald-50 border-t border-emerald-200 px-4 py-2.5 flex items-center gap-2"
           data-testid="payment-readiness-collected">
        <CheckCheck className="h-4 w-4 text-emerald-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-600 truncate">{line}</p>
          <p className="text-[11px] text-emerald-800 mt-0.5" data-testid="readiness-deposit-collected">
            {t(`${K}.blocker.DEPOSIT_ALREADY_COLLECTED`)}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-amber-50 border-t border-amber-200 px-4 py-2.5"
         data-testid="payment-readiness-blocked">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-600 truncate">{line}</p>
          {blockers.map((b) => (
            <p key={b.code} className="text-[11px] text-amber-800 mt-0.5"
               data-testid={`readiness-blocker-${b.code}`}>
              {t(`${K}.blocker.${b.code}`, {
                defaultValue: t(`${K}.blocker.GENERIC`),
                currency: b.charge_currency || currency,
                settlementCurrency: b.settlement_currency || '',
              })}
              {' '}
              <span className="text-amber-700">
                {t(`${K}.actor.${b.actor}`, { defaultValue: '' })}
              </span>
            </p>
          ))}
          {!blockers.length && (
            <p className="text-[11px] text-amber-800 mt-0.5"
               data-testid="readiness-blocker-GENERIC">
              {error || t(`${K}.checkFailed`)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="ghost" size="sm" onClick={load}
                  className="text-[11px] h-7 px-2 text-amber-800"
                  data-testid="readiness-recheck-btn">
            <RefreshCw className="h-3 w-3 mr-1" />{t(`${K}.recheck`)}
          </Button>
          {link && (
            <button type="button" onClick={() => { window.location.hash = `#${link}`; }}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-900 underline"
                    data-testid="readiness-fix-link">
              {t(`${K}.goToSetup`)}<ArrowRight className="h-3 w-3" />
            </button>
          )}
          <Button disabled className="bg-slate-300 text-white text-xs px-4 h-8 cursor-not-allowed"
                  data-testid="request-payment-btn-blocked">
            <DollarSign className="h-3 w-3 mr-1" />
            {t('provider.messaging.requestPayment')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentReadinessBar;
